import React, { useEffect, useRef, useState } from 'react'
import GoogleMapReact, { Coords } from 'google-map-react'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import Supercluster, {
	ClusterFeature,
	PointFeature,
	AnyProps
} from 'supercluster'

import * as styles from './styles.module.scss'
import mapStyles from './assets/mapStyles.json'
import Marker from './Marker'
import branches from './assets/branches'
import markerQuestionMark from '../../2021/images/marker-question-mark.svg'
import countryCoordinates from './assets/countryCoordinates'
import funFactsCountries from './assets/funFactsCountries'
import bestsellers from './assets/bestsellers'
import markerDistribution from '../../2021/images/marker-distribution.svg'
import { animated, useSpring } from 'react-spring'
import Cluster from './Cluster'
import countryBorders from './assets/countryBorders.json'

const TAB_ZAJIMAVOSTI = 0
const TAB_VUNE = 1
const TAB_PRODEJNY = 2

const tabLeftOffset: { [key: string]: number } = {
	[TAB_ZAJIMAVOSTI]: 0,
	[TAB_VUNE]: 33.3,
	[TAB_PRODEJNY]: 66.6
}

const Map: React.FC = () => {
	const { t, i18n } = useTranslation()
	const [tab, setTab] = useState(TAB_ZAJIMAVOSTI)
	const [clusters, setClusters] = useState<
		(PointFeature<AnyProps> | ClusterFeature<AnyProps>)[]
	>([])
	const map = useRef<any>()
	const maps = useRef<any>()
	const superclusterIndex = useRef<Supercluster<AnyProps, AnyProps>>()

	useEffect(() => {
		superclusterIndex.current = new Supercluster({
			radius: 30,
			minZoom: 0,
			maxZoom: 16
		})
		superclusterIndex.current?.load(
			branches.map((branch) => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [branch.lng, branch.lat]
				},
				properties: {
					city: branch.city,
					title: branch.title,
					distribution: branch.distribution
				}
			}))
		)
	}, [])

	const handleClick = (
		e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
		tab: number
	) => {
		e.preventDefault()
		setTab(tab)
	}

	const tabStyle = useSpring({
		left: `${tabLeftOffset[tab]}%`,
		config: { mass: 1, tension: 400, friction: 30 }
	})

	const onMapChange = (value?: GoogleMapReact.ChangeEventValue): void => {
		const bounds = value?.bounds
		if (!bounds) return

		const clusters = superclusterIndex.current?.getClusters(
			[bounds.sw.lng, bounds.sw.lat, bounds.ne.lng, bounds.ne.lat],
			value?.zoom
		)

		setClusters(clusters ?? [])
	}

	const onGoogleApiLoaded = (_map: any, _maps: any) => {
		map.current = _map
		maps.current = _maps

		_map.data.addGeoJson(countryBorders)
		_map.data.setStyle(() => ({
			fillColor: '#dc0069',
			fillOpacity: 1,
			strokeColor: '#FFFFFF',
			strokeWeight: 1
		}))
	}

	const zoom = (lat: number, lng: number, newZoom?: number) => {
		map.current.setZoom(newZoom ?? map.current.getZoom() + 1)
		map.current.panTo({ lat, lng })
	}

	return (
		<>
			<div className="container mt-8 overflow-auto">
				<ul className={`list-unstyled mb-4 row ${styles.tabs}`}>
					<animated.li className={styles.tabActive} style={tabStyle} />
					<li className="col text-center">
						<a
							onClick={(e) => handleClick(e, TAB_ZAJIMAVOSTI)}
							href="#"
							className={`${styles.tabItem} ${
								tab === TAB_ZAJIMAVOSTI ? styles.tabItemActive : undefined
							}`}
						>
							{t('map.tabs.facts')}
						</a>
					</li>
					<li className="col text-center">
						<a
							onClick={(e) => handleClick(e, TAB_VUNE)}
							href="#"
							className={`${styles.tabItem} ${
								tab === TAB_VUNE ? styles.tabItemActive : undefined
							}`}
						>
							{t('map.tabs.bestsellers')}
						</a>
					</li>
					<li className="col text-center">
						<a
							onClick={(e) => handleClick(e, TAB_PRODEJNY)}
							href="#"
							className={`${styles.tabItem} ${
								tab === TAB_PRODEJNY ? styles.tabItemActive : undefined
							}`}
						>
							{t('map.tabs.branches')}
						</a>
					</li>
				</ul>
			</div>

			<section className={styles.mapWrapper}>
				<GoogleMapReact
					bootstrapURLKeys={{ key: 'AIzaSyCA6GheGsE1hWJiXvHTFnSS5rOTrCmU5pU' }}
					defaultCenter={{ lat: 49.8037633, lng: 15.4749126 }}
					defaultZoom={5}
					yesIWantToUseGoogleMapApiInternals
					onChange={onMapChange}
					onGoogleApiLoaded={({ map, maps }) => onGoogleApiLoaded(map, maps)}
					options={{
						styles: mapStyles,
						scrollwheel: false
					}}
				>
					<>
						<Marker lat={49.5} lng={15.5} />
					</>
					{tab === TAB_ZAJIMAVOSTI &&
						Object.keys(funFactsCountries).map((countryCode, index) => {
							const country = countryCoordinates[countryCode]

							return (
								<Marker
									lat={country.lat}
									lng={country.lng}
									key={index}
									content={
										<>{t(`map.funFacts.${countryCode.toLowerCase()}`)}</>
									}
									markerImg={markerQuestionMark}
								/>
							)
						})}

					{tab === TAB_VUNE &&
						Object.entries(bestsellers).map(
							([countryCode, bestseller], index) => {
								const country = countryCoordinates[countryCode]

								return (
									<Marker
										lat={country.lat}
										lng={country.lng}
										key={index}
										content={
											<>
												{i18n.language === 'cs'
													? country.name_cz
													: country.name}
											</>
										}
										markerImg={`/2021/images/bestsellers/${countryCode.toLowerCase()}.jpg`}
										url={bestseller.url}
										bestseller
									/>
								)
							}
						)}

					{tab === TAB_PRODEJNY &&
						clusters.map((cluster, index) =>
							cluster.properties.cluster ? (
								<Cluster
									lat={cluster.geometry.coordinates[1]}
									lng={cluster.geometry.coordinates[0]}
									key={index}
									onClick={() => {
										zoom(
											cluster.geometry.coordinates[1],
											cluster.geometry.coordinates[0],
											typeof cluster.id === 'number'
												? superclusterIndex.current?.getClusterExpansionZoom(
														cluster.id
												  )
												: undefined
										)
									}}
								>
									{cluster.properties.point_count}
								</Cluster>
							) : (
								<Marker
									lat={cluster.geometry.coordinates[1]}
									lng={cluster.geometry.coordinates[0]}
									key={index}
									markerImg={
										cluster.properties.distribution
											? markerDistribution
											: undefined
									}
									content={
										<>
											{cluster.properties.city}
											<br />
											<strong className="h5">
												{t(cluster.properties.title)}
											</strong>
										</>
									}
								/>
							)
						)}
				</GoogleMapReact>
			</section>
		</>
	)
}

export default Map
